import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Small,
  TextArea,
} from "orcs-design-system";

import OptionWritesBlockedPopover from "src/pages/Designs/OptionWritesBlockedPopover";
import type { RefusalReason } from "src/pages/WorkforceGuardrails/ruleShape";

import type { ApprovalGroup } from "./RequisitionGuardrailOutcome";
import type {
  ApprovalResponse,
  ChainActions,
  DecisionInput,
  RefusalCatalog,
} from "./useGuardrailChainActions";

const hasComment = (comment: string) => comment.trim() !== "";

const selectedNeedsComment = (
  reasons: RefusalReason[],
  selectedIds: readonly string[]
) => {
  const selected = new Set(selectedIds);
  return reasons.some(
    (reason) => reason.commentRequired === true && selected.has(reason.id)
  );
};

const refuseReady = (
  catalog: RefusalCatalog,
  selectedIds: readonly string[],
  comment: string
) => {
  if (!catalog.ready) {
    return false;
  }
  if (catalog.reasons.length === 0) {
    return hasComment(comment);
  }
  if (selectedIds.length === 0) {
    return false;
  }
  if (selectedNeedsComment(catalog.reasons, selectedIds)) {
    return hasComment(comment);
  }
  return true;
};

const decisionInput = (
  reasons: RefusalReason[],
  selectedIds: readonly string[],
  comment: string
): DecisionInput => {
  const known = new Set(reasons.map((reason) => reason.id));
  const reasonIds = selectedIds.filter((id) => known.has(id));
  const text = comment.trim();
  return {
    ...(reasonIds.length > 0 ? { reasonIds } : {}),
    ...(text ? { comment: text } : {}),
  };
};

const refuseHint = (catalog: RefusalCatalog): string => {
  if (!catalog.ready) {
    return "";
  }
  if (catalog.reasons.length === 0) {
    return "A comment is required to refuse.";
  }
  const needComment = catalog.reasons
    .filter((reason) => reason.commentRequired === true)
    .map((reason) => reason.label);
  const base = "Select reason(s) if refusing.";
  if (needComment.length === 0) {
    return base;
  }
  return `${base} A comment is required for: ${needComment.join(", ")}.`;
};

const GuardrailDecisionForm = ({
  group,
  order,
  actions,
  layout,
}: {
  group: ApprovalGroup;
  order: number;
  actions: ChainActions;
  layout: "inline" | "footer";
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  if (!group.canRespond) {
    return null;
  }
  const enabled = refuseReady(actions.catalog, selectedIds, comment);
  const send = (response: ApprovalResponse) => {
    if (actions.saving) {
      return;
    }
    if (response === "REFUSE" && !enabled) {
      return;
    }
    actions.respondTo(
      order,
      group.approverGroupId,
      response,
      decisionInput(actions.catalog.reasons, selectedIds, comment)
    );
  };
  const toggle = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  };
  const hint = layout === "footer" ? refuseHint(actions.catalog) : "";
  return (
    <Flex flexDirection="column" gap="xs" width="100%">
      {hint ? <Small color="greyDark">{hint}</Small> : null}
      {actions.catalog.reasons.map((reason) => (
        <Checkbox
          key={reason.id}
          name={`guardrail-refusal-${order}-${group.approverGroupId}-${reason.id}`}
          label={reason.label}
          checked={selectedIds.includes(reason.id)}
          onChange={() => toggle(reason.id)}
        />
      ))}
      <TextArea
        id={`guardrail-decision-comment-${order}-${group.approverGroupId}`}
        label="Comment"
        fullWidth
        rows={layout === "footer" ? "3" : "2"}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
      />
      <DecisionButtons
        layout={layout}
        saving={actions.saving}
        writesBlocked={actions.writesBlocked}
        refuseEnabled={enabled}
        onApprove={() => send("APPROVE")}
        onRefuse={() => send("REFUSE")}
      />
    </Flex>
  );
};

const DecisionButtons = ({
  layout,
  saving,
  writesBlocked,
  refuseEnabled,
  onApprove,
  onRefuse,
}: {
  layout: "inline" | "footer";
  saving: boolean;
  writesBlocked: boolean;
  refuseEnabled: boolean;
  onApprove: () => void;
  onRefuse: () => void;
}) => {
  switch (layout) {
    case "inline":
      return (
        <OptionWritesBlockedPopover blocked={writesBlocked}>
          <Flex gap="s">
            <Button
              small
              type="button"
              disabled={saving || writesBlocked}
              onClick={onApprove}
            >
              Approve
            </Button>
            <Button
              small
              type="button"
              variant="danger"
              disabled={saving || writesBlocked || !refuseEnabled}
              onClick={onRefuse}
            >
              Refuse
            </Button>
          </Flex>
        </OptionWritesBlockedPopover>
      );
    case "footer":
      return (
        <OptionWritesBlockedPopover blocked={writesBlocked}>
          <Flex gap="s" alignItems="stretch">
            <Box flex="1" minWidth="0">
              <Button
                variant="success"
                fullWidth
                type="button"
                leftIcon={["fas", "check"]}
                disabled={saving || writesBlocked}
                onClick={onApprove}
              >
                Approve
              </Button>
            </Box>
            <Button
              type="button"
              variant="dangerAlternate"
              disabled={saving || writesBlocked || !refuseEnabled}
              onClick={onRefuse}
            >
              Refuse
            </Button>
          </Flex>
        </OptionWritesBlockedPopover>
      );
    default: {
      const unexpected: never = layout;
      return unexpected;
    }
  }
};

export default GuardrailDecisionForm;
